import type React from 'react';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { Menu, MenuItem, TableCell, TableRow, Tooltip } from '@mui/material';
import { Label } from '@components/Label';
import { Button } from '@components/index';
import { TableBodyCell } from '@components/table';
import { useStore } from '@stores/RootStore';
import { ThreeDotIcon } from '@icons/ThreeDotIcon';
import { useTextOverflowDetection } from '@customHooks/useTextOverflowDetection';
import { useDeleteMonthlyBilling, useGetMonthlyBilling } from '@queryHooks/useMonthlyBilling';
import { useGetUsageMetricList } from '@queryHooks/useSoftware';
import { useGetSubscription } from '@queryHooks/useSubscription';
import { useGetTenant } from '@queryHooks/useTenant';
import { dateFormat } from '@utils/dateUtil';
import { currencyNumberFormat } from '@utils/numberFormat';
import type { SubscriptionBillingModel } from '@models/SubscriptionBillingModel';
import { MetricUsageCostModel } from '@repositories/billingOptimizationRepository/models/MetricUsageCostModel';
import { usePrintButton } from '../detail/PrintButton';
import { SubscriptionBillingPrintView } from '../detail/SubscriptionBillingDetail/SubscriptionBillingPrintView';

type Props = {
  rowData: SubscriptionBillingModel;
};

export const SubscriptionBillingRow = forwardRef(({ rowData }: Props, ref: React.ForwardedRef<HTMLTableRowElement>) => {
  const {
    uiStore: { toastStore },
  } = useStore();
  const { tenantId } = useParams();
  const { t } = useTranslation();
  const {
    uiStore: { alertStore },
  } = useStore();
  const { colors } = useTheme();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>();
  const printViewRef = useRef<HTMLDivElement>(null);

  const { printState, onBeforeGetContentResolve, PrintButton } = usePrintButton();
  const [isItemOverflow, itemCallbackRef] = useTextOverflowDetection();
  const [isRemarkOverflow, remarkCallbackRef] = useTextOverflowDetection();

  const { data: tenant } = useGetTenant(tenantId ?? '');
  const { data: subscriptionBilling, isSuccess } = useGetMonthlyBilling(rowData.id, { enabled: printState.isOpen });
  const { data: usageMetrics, isSuccess: isUsageMetricsSuccess } = useGetUsageMetricList(
    rowData.subscription.software.id ?? '',
    { enabled: printState.isOpen },
  );
  const { data: subscription, isSuccess: isSubscriptionSuccess } = useGetSubscription(
    subscriptionBilling?.subscription.subscriptionId ?? '',
  );
  const { mutate: deleteSubscriptionBilling } = useDeleteMonthlyBilling();

  const metricUsageCosts = MetricUsageCostModel.createFromMetric(
    usageMetrics ?? [],
    subscriptionBilling?.moduleUsageCosts,
  );

  useEffect(() => {
    if (
      printState.isOpen &&
      isSuccess &&
      isUsageMetricsSuccess &&
      isSubscriptionSuccess &&
      typeof onBeforeGetContentResolve.current === 'function'
    ) {
      onBeforeGetContentResolve.current();
      setAnchorEl(undefined);
    }
  }, [isSubscriptionSuccess, isSuccess, isUsageMetricsSuccess, onBeforeGetContentResolve, printState.isOpen]);

  const handleBillingClick = () => {
    navigate(`subscription/${rowData.id}`);
  };

  const handleThreeDotClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(undefined);
  };

  const handleDeleteClick = () => {
    setAnchorEl(undefined);
    alertStore.open({
      title: t('Acc_Del_01'),
      message: t('Acc_Del_02'),
      confirmName: t('Subscrib_Detail_BuyRequest_12'),
      onConfirm: () => {
        deleteSubscriptionBilling(rowData.id, {
          onSuccess: () => {
            toastStore.open({ message: t('Acc_Del_03'), hideAction: true });
          },
        });
      },
      useCancel: true,
      type: 'error',
    });
  };

  return (
    <>
      <BillingRow ref={ref} onClick={handleBillingClick}>
        <TableCell>{dateFormat(rowData.month, '-', t('DateFormat_YM'))}</TableCell>
        <Tooltip
          title={`${rowData.subscription.software.name}(${rowData.subscription.name})`}
          disableHoverListener={!isItemOverflow}
          arrow
        >
          <TableBodyCell ref={itemCallbackRef}>
            {rowData.subscription.software.name}({rowData.subscription.name})
          </TableBodyCell>
        </Tooltip>
        <TableCell>
          <Label variant="solid" color="orange">
            {t('Acc_Main_25')}
          </Label>
        </TableCell>
        <TableCell>
          {dateFormat(rowData.startDate, '-', t('DateFormat_YMD'))} ~{' '}
          {dateFormat(rowData.endDate, '-', t('DateFormat_YMD'))}
        </TableCell>
        <TableCell sx={{ paddingRight: '8px' }}>
          {currencyNumberFormat(rowData.originalCost, rowData.originalCurrencyUnit, 'text')}
        </TableCell>
        <TableCell>
          {tenant?.currencyUnit ? currencyNumberFormat(rowData.cost, rowData.currencyUnit, 'text') : '-'}
        </TableCell>
        <TableCell>{dateFormat(rowData?.paymentDate, '-', t('DateFormat_YMD'))}</TableCell>
        <TableCell>
          <Label variant="solid" color={rowData.statusLabelColor}>
            {rowData.statusLabel}
          </Label>
        </TableCell>
        <Tooltip
          title={rowData.isManuallyEdited ? t('Acc_Main_34') : '-'}
          disableHoverListener={!isRemarkOverflow}
          arrow
        >
          <TableBodyCell ref={remarkCallbackRef}>
            {rowData.isManuallyEdited ? (
              <Label variant="solid" color="white">
                {t('Acc_Main_34')}
              </Label>
            ) : (
              '-'
            )}
          </TableBodyCell>
        </Tooltip>
        <TableCell align="center">
          <ThreeDotButton variant="text" size="small" onClick={handleThreeDotClick}>
            <ThreeDotIcon width={18} height={18} color={colors['ic-gray-dark']} />
          </ThreeDotButton>
        </TableCell>
      </BillingRow>

      <StyledMenu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: '160px' } }}
      >
        <StyledMenuItem disabled={printState.isOpen}>
          <PrintButton
            contentRef={printViewRef}
            // eslint-disable-next-line react/no-unstable-nested-components
            trigger={() => <span>{t('Acc_Detail_16')}</span>}
            documentTitle={`${subscriptionBilling?.subscription.name}_${dateFormat(
              subscriptionBilling?.month,
              '',
              t('DateFormat_YM'),
            )}`}
          />
        </StyledMenuItem>
        <DeleteMenuItem disabled={!rowData.isExternalSubscription} onClick={handleDeleteClick}>
          {t('Acc_Del_01')}
        </DeleteMenuItem>
      </StyledMenu>

      {isSubscriptionSuccess && printState.isOpen && (
        <SubscriptionBillingPrintView
          subscription={subscription}
          ref={printViewRef}
          subscriptionBilling={rowData}
          metricUsageCosts={metricUsageCosts}
        />
      )}
    </>
  );
});

const BillingRow = styled(TableRow)`
  cursor: pointer;
`;

const ThreeDotButton = styled(Button)`
  width: 100%;
  height: 100%;
  cursor: pointer;
`;

const StyledMenu = styled(Menu)`
  & .MuiPaper-root {
    box-shadow: none;
    border-radius: 0;
  }
  & .MuiList-root {
    border-radius: 2px;
  }
`;

const StyledMenuItem = styled(MenuItem)`
  &:active {
    color: ${({ theme: { colors } }) => colors['text-primary-light']};
  }
`;

const DeleteMenuItem = styled(MenuItem)`
  color: ${({ theme: { colors } }) => colors['text-red-light']};
  &:active {
    color: ${({ theme: { colors } }) => colors['text-primary-light']};
  }
`;
